import type { CSSProperties, FC } from 'react';
import { memo } from 'react';
import TopWinFeaturedCard from '../TopWinFeaturedCard';
import TopWinCard from '../TopWinCard';
import { useTopWins } from '../../hooks/useTopWins';
import { useVisibleLayout } from '../../hooks/useVisibleLayout';
import { useRotatingWinsQueue } from '../../hooks/useRotatingWinsQueue';
import type { RotatingWin } from '../../hooks/useRotatingWinsQueue';
import styles from './TopWinsSection.module.css';

interface SlotProps {
  item: RotatingWin;
  col: number;
  row: number;
  isExiting?: boolean;
}

const Slot: FC<SlotProps> = ({ item, col, row, isExiting = false }) => {
  const style = { '--slot-col': col, '--slot-row': row } as CSSProperties;
  return (
    <div className={styles.slot} style={style} data-exiting={isExiting ? 'true' : 'false'}>
      <TopWinCard win={item.win} />
    </div>
  );
};

interface TopWinsSectionProps {
  /** Freezes the slot rotation after the initial fill (visual-regression stories only). */
  rotationPaused?: boolean;
}

const TopWinsSection: FC<TopWinsSectionProps> = ({ rotationPaused = false }) => {
  const { featured, recent } = useTopWins();
  const { cols, rows } = useVisibleLayout();
  const { visible, exiting } = useRotatingWinsQueue(recent, cols * rows, rotationPaused);

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        {featured && <TopWinFeaturedCard win={featured} />}
        <div className={styles.scrollContainer}>
          <div className={styles.grid}>
            {visible.map((item, pos) => (
              <Slot key={item.seq} item={item} col={Math.floor(pos / rows)} row={pos % rows} />
            ))}
            {exiting && <Slot key={exiting.seq} item={exiting} col={cols - 1} row={rows} isExiting />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(TopWinsSection);
